import { ref, reactive, watch } from 'vue'
import { defineStore } from 'pinia'
import API from "../services";
import type { VideoDetail, PhotoFile, MissionPlanResponse, Evento, UnitTrackPoint, MosaicLatest } from '../types';

export const useGlobalStore = defineStore('global', () => {

    const isMobile = ref(false);
    const isEmbeddedInMobileApp = ref(false);
    const zoom = ref(11);
    const coordenadas = ref([0, 0])
    const elevation = ref(0)
    const prediosLayer = ref(false)
    const regionesLayer = ref(false)
    const comunasLayer = ref(false)
    const ciudadesLayer = ref(false);
    const carreterasLayer = ref(false);
    const caminosPrincipalesLayer = ref(false);
    const caminosVecinalesLayer = ref(false);
    const aguasLayer = ref(false);
    const isEvents = ref(false)
    const isImageLibrary = ref(false)
    const isMosaicLibrary = ref(false)
    const isOrdersLibrary = ref(false)
    const loadedPhotos = ref(false)
    const isVideoLibrary = ref(false)
    const isSearchModule = ref(false)
    const currentVideoId = ref<number | null>(null);
    const currentVideoDetail = ref<VideoDetail | null>(null);
    const videoDetails = ref<VideoDetail[]>([]);
    const selectedPhoto = ref<PhotoFile | null>(null);
    const isMissionPlan = ref(false);
    const viewCurrentMissionPlan = ref(false);
    const viewCurrentMissionEvents = ref(false);
    const viewLatestMosaic = ref(true);
    const missionPlan = ref<MissionPlanResponse | null>(null);
    const missionEvents = ref<Evento[]>([]);
    const selectedMosaicId = ref<number | null>(null);

    // Clipboard de coordenadas - almacena las últimas coordenadas copiadas
    const coordinatesClipboard = ref<{
        lat: number;
        lon: number;
        timestamp: Date;
        source?: string;
    } | null>(null);

    // Variables para mosaicos
    const latestMosaics = ref<MosaicLatest[]>([]);
    const mosaicPhotosMap = ref<Map<number, any[]>>(new Map());

    // Variable reactiva para patrulla
    const patrulla = reactive({
        activeMission: 0 as number,
        reportCoords: false as boolean,
        unitId: '' as string,
        useApp: false as boolean
    });

    // Tracks por unidad - almacena un arreglo de puntos de track para cada unidad
    const unitTracks = ref<Record<string, UnitTrackPoint[]>>({});

    // Control de visualización de tracks de misión por unidad
    const viewUnitMissionTrack = ref<Record<string, boolean>>({});

    const predio = ref("")
    const filters = reactive({
        visible: "1",
        showType: false,
        type: "",
        showState: false,
        state: "",
        showScope: false,
        scope: "",
        showStartDate: false,
        start_date: "",
        showEndDate: false,
        end_date: "",
        showZona: false,
        lat_zona: "",
        lon_zona: "",
        rad_zona: "10",
        showPredio: false,
        predio_name: "",
        predio_rad: "10",
        showNumber: false,
        inicio_rango: "",
        fin_rango: "",
        showEvent: false,
        event: "",
    });
    // Variable reactiva para coordenadas
    const screenBounds = ref({
        _northEast: {
            lat: 0,
            lng: 0
        },
        _southWest: {
            lat: 0,
            lng: 0
        }
    });

    // Estado para posiciones y tamaños de modales
    const modalsState = reactive<{ [key: string]: { top: string, left: string, width: string, height: string } }>({})

    // Al iniciar, intenta cargar el estado guardado
    const savedModalsState = localStorage.getItem('modalsState')
    if (savedModalsState) {
        try {
            const parsed = JSON.parse(savedModalsState)
            Object.assign(modalsState, parsed)
        } catch (e) {
            // Error parsing saved modals state; ignore and use default
        }
    }

    const selectedVideoMarker = ref<number | null>(null);

    function setModalState(modalId: string, state: { top?: string, left?: string, width?: string, height?: string }) {
        if (!modalsState[modalId]) {
            modalsState[modalId] = { top: '50px', left: '50px', width: '600px', height: '400px' }
        }
        Object.assign(modalsState[modalId], state)
    }

    // Guarda el estado de los modales en localStorage cada vez que cambie
    watch(
        modalsState,
        (val) => {
            localStorage.setItem('modalsState', JSON.stringify(val))
        },
        { deep: true }
    )
    async function setElevation(lat: string, lng: string) {
        API.getElevation(lat, lng).then((response) => {
            elevation.value = response
        })
    }

    // Funciones para manejar los tracks de las unidades
    function addUnitTrackPoint(unitId: string, trackPoint: UnitTrackPoint) {
        if (!unitTracks.value[unitId]) {
            unitTracks.value[unitId] = []
        }
        unitTracks.value[unitId].push(trackPoint)
    }

    function getUnitTrack(unitId: string): UnitTrackPoint[] {
        return unitTracks.value[unitId] || []
    }

    function clearUnitTrack(unitId: string) {
        if (unitTracks.value[unitId]) {
            unitTracks.value[unitId] = []
        }
    }

    function clearAllTracks() {
        unitTracks.value = {}
    }

    function initUnitTrack(unitId: string, trackPoints: UnitTrackPoint[]) {
        unitTracks.value[unitId] = [...trackPoints]
    }

    function addMultipleTrackPoints(unitId: string, trackPoints: UnitTrackPoint[]) {
        if (!unitTracks.value[unitId]) {
            unitTracks.value[unitId] = []
        }
        unitTracks.value[unitId].push(...trackPoints)
    }

    // Funciones para manejar la visualización de tracks por unidad
    function setUnitTrackVisibility(unitId: string, visible: boolean) {
        viewUnitMissionTrack.value[unitId] = visible
    }

    function getUnitTrackVisibility(unitId: string): boolean {
        return viewUnitMissionTrack.value[unitId] || false
    }

    function toggleUnitTrackVisibility(unitId: string): boolean {
        const currentValue = viewUnitMissionTrack.value[unitId] || false
        viewUnitMissionTrack.value[unitId] = !currentValue
        return !currentValue
    }

    function hideAllUnitTracks() {
        viewUnitMissionTrack.value = {}
    }

    // Funciones para manejar el clipboard de coordenadas
    function copyCoordinatesToClipboard(lat: number, lon: number, source?: string) {
        coordinatesClipboard.value = {
            lat,
            lon,
            timestamp: new Date(),
            source: source || 'unknown'
        }
        console.log(`📋 Coordenadas guardadas en clipboard: ${lat}, ${lon} (${source})`)
    }

    function getClipboardCoordinates() {
        return coordinatesClipboard.value
    }

    function clearCoordinatesClipboard() {
        coordinatesClipboard.value = null
    }

    function getClipboardCoordinatesAsString(): string | null {
        if (!coordinatesClipboard.value) return null
        return `${coordinatesClipboard.value.lat}, ${coordinatesClipboard.value.lon}`
    }

    // Funciones para manejar mosaicos
    function setLatestMosaics(mosaics: MosaicLatest[]) {
        latestMosaics.value = mosaics
    }

    function addMosaicPhotos(mosaicId: number, photos: any[]) {
        mosaicPhotosMap.value.set(mosaicId, photos)
    }

    function getMosaicPhotos(mosaicId: number): any[] {
        return mosaicPhotosMap.value.get(mosaicId) || []
    }

    function clearMosaics() {
        latestMosaics.value = []
        mosaicPhotosMap.value.clear()
    }

    return {
        isMobile,
        isEmbeddedInMobileApp,
        zoom,
        coordenadas,
        elevation,
        prediosLayer,
        regionesLayer,
        comunasLayer,
        ciudadesLayer,
        carreterasLayer,
        caminosPrincipalesLayer,
        caminosVecinalesLayer,
        aguasLayer,
        predio,
        filters,
        screenBounds,
        setElevation,
        isEvents,
        isImageLibrary,
        isMosaicLibrary,
        loadedPhotos,
        isVideoLibrary,
        currentVideoId,
        currentVideoDetail,
        videoDetails,
        selectedPhoto,
        modalsState,
        setModalState,
        selectedVideoMarker,
        missionPlan,
        missionEvents,
        isMissionPlan,
        isOrdersLibrary,
        viewCurrentMissionPlan,
        viewCurrentMissionEvents,
        viewLatestMosaic,
        viewUnitMissionTrack,
        // Variables y funciones para tracks de unidades
        unitTracks,
        addUnitTrackPoint,
        getUnitTrack,
        clearUnitTrack,
        clearAllTracks,
        initUnitTrack,
        addMultipleTrackPoints,
        // Funciones para control de visibilidad de tracks
        setUnitTrackVisibility,
        getUnitTrackVisibility,
        toggleUnitTrackVisibility,
        hideAllUnitTracks,
        // Variables y funciones para clipboard de coordenadas
        coordinatesClipboard,
        copyCoordinatesToClipboard,
        getClipboardCoordinates,
        clearCoordinatesClipboard,
        getClipboardCoordinatesAsString,
        // Variables y funciones para mosaicos
        latestMosaics,
        mosaicPhotosMap,
        setLatestMosaics,
        selectedMosaicId,
        addMosaicPhotos,
        getMosaicPhotos,
        clearMosaics,
        patrulla,
        isSearchModule
    };
})
